import React from 'react';
import { format, parseISO } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AssessmentResults = ({ assessments, onNavigateToHistory }) => {
  const getAssessmentTypeLabel = (type) => {
    const labels = {
      'roi_calculator': 'ROI Calculator',
      'data_maturity': 'Data Maturity Assessment'
    };
    return labels?.[type] || type;
  };

  const getAssessmentIcon = (type) => {
    const icons = {
      'roi_calculator': 'DollarSign',
      'data_maturity': 'BarChart3'
    };
    return icons?.[type] || 'FileText';
  };

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy');
    } catch {
      return 'Date not available';
    }
  };

  if (!assessments || assessments?.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-elevation-1 p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-[#002B45]">Assessment Results</h3>
        </div>
        <div className="text-center py-12">
          <Icon name="FileText" size={48} className="text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No assessments completed yet</p>
          <p className="text-sm text-gray-400 mt-2">Complete an assessment to see your results here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-elevation-1 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
            <Icon name="FileText" size={20} className="text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-[#002B45]">Assessment Results Archive</h3>
            <p className="text-sm text-gray-500">{assessments?.length} assessments completed</p>
          </div>
        </div>
        <Button
          onClick={onNavigateToHistory}
          variant="outline"
          size="sm"
          iconName="BarChart3"
          iconPosition="left"
        >
          View Analytics
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {assessments?.map((assessment) => (
          <div
            key={assessment?.id}
            className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-300"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-12 h-12 bg-gradient-to-br from-[#1FB5A5] to-[#002B45] rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name={getAssessmentIcon(assessment?.assessmentType)} size={24} className="text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-[#002B45] mb-1">{assessment?.title}</h4>
                <p className="text-xs text-gray-500 mb-2">
                  {getAssessmentTypeLabel(assessment?.assessmentType)}
                </p>
                <p className="text-xs text-gray-400">
                  Completed: {formatDate(assessment?.createdAt)}
                </p>
              </div>
            </div>

            {assessment?.results && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-gray-700 mb-2">Key Results:</p>
                  {assessment?.results?.overallScore && (
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">Overall Score</span>
                      <span className="text-lg font-bold text-[#1FB5A5]">
                        {assessment?.results?.overallScore}
                      </span>
                    </div>
                  )}
                  {assessment?.results?.maturityLevel && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Maturity Level</span>
                      <span className="text-sm font-semibold text-[#002B45]">
                        {assessment?.results?.maturityLevel}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="bg-blue-50 rounded-lg p-4 flex items-start gap-3">
          <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-900 mb-1">Compare Your Progress</p>
            <p className="text-xs text-blue-700">
              Visit the Assessment History & Analytics page to compare results across time periods and track your improvement journey.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentResults;